// src/pages/shop/ShopAiHistory.jsx
//
// Полный список AI генераций магазина.
// Фильтр по типу. При клике на генерацию — открывается превью с действиями:
//   - Скачать / Сохранить как фото товара
//   - Удалить (visual hide, на бэке остаётся для аудита)
//
// URL ?focus=<id> — открывает превью этой генерации сразу.

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ai as aiApi } from '../../api';
import Icon from '../../components/Icon';

const API_HOST = (() => {
  try {
    const u = new URL(process.env.REACT_APP_API_URL || 'https://api.alsatuu.biz/api');
    return `${u.protocol}//${u.host}`;
  } catch { return 'https://api.alsatuu.biz'; }
})();
function fullUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return API_HOST + u;
}

const TYPES = ['', 'try_on', 'studio_photo', 'on_pedestal'];

export default function ShopAiHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    aiApi.history(100)
      .then(r => {
        const list = r.items || [];
        setItems(list);
        if (focusId) {
          const found = list.find(g => g.id === focusId);
          if (found) setPreview(found);
        }
      })
      .finally(() => setLoading(false));
  }, [focusId]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter
    ? items.filter(i => i.type === filter)
    : items;

  return (
    <div className="page" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', flex: 1 }}>
          {t('shopAi.historyTitle')}
        </h1>
      </div>

      {/* Фильтр */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {TYPES.map(tt => (
          <button
            key={tt || 'all'}
            onClick={() => setFilter(tt)}
            style={{
              padding: '6px 12px', fontSize: 13,
              borderRadius: 999,
              background: filter === tt ? 'var(--brand-primary)' : 'var(--surface)',
              color: filter === tt ? '#fff' : 'var(--fg)',
              border: '1px solid ' + (filter === tt ? 'var(--brand-primary)' : 'var(--border)'),
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {tt === ''             ? t('shopAi.filterAll')   :
             tt === 'try_on'       ? t('aiGenerate.typeTryOn')    :
             tt === 'studio_photo' ? t('aiGenerate.typeStudio')   :
                                     t('aiGenerate.typePedestal')}
          </button>
        ))}
      </div>

      {loading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>
          <Icon name="sparkles" size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>{t('shopAi.empty')}</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {filtered.map(g => (
            <button
              key={g.id}
              onClick={() => setPreview(g)}
              style={{
                aspectRatio: '3/4',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                overflow: 'hidden',
                position: 'relative',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              {g.outputUrl && g.status === 'success' ? (
                <img src={fullUrl(g.outputUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: g.status === 'failed' ? 'var(--danger)' : 'var(--fg-muted)',
                  fontSize: 11, textAlign: 'center', padding: 8,
                }}>
                  {g.status === 'failed' ? t('shopAi.failed') : t('shopAi.processing')}
                </div>
              )}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.7)',
                color: '#fff', fontSize: 10, padding: '3px 6px',
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span>{t(`aiGenerate.type${g.type === 'try_on' ? 'TryOn' : g.type === 'studio_photo' ? 'Studio' : 'Pedestal'}`)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Превью */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex', flexDirection: 'column',
            padding: 16,
          }}
        >
          <button
            onClick={() => setPreview(null)}
            style={{
              alignSelf: 'flex-end', background: 'rgba(255,255,255,0.1)',
              border: 'none', borderRadius: '50%',
              width: 36, height: 36, color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name="x" size={20} />
          </button>

          <div
            onClick={e => e.stopPropagation()}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 16,
            }}
          >
            {preview.outputUrl ? (
              <img
                src={fullUrl(preview.outputUrl)}
                alt=""
                style={{
                  maxWidth: '100%', maxHeight: '60vh',
                  borderRadius: 'var(--r-md)',
                }}
              />
            ) : (
              <div style={{ color: '#fff' }}>{t('shopAi.failed')}</div>
            )}

            <div style={{ color: '#fff', fontSize: 13, textAlign: 'center' }}>
              {t(`aiGenerate.type${preview.type === 'try_on' ? 'TryOn' : preview.type === 'studio_photo' ? 'Studio' : 'Pedestal'}`)}
              <br />
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                {new Date(preview.createdAt).toLocaleString()}
              </span>
            </div>

            {preview.outputUrl && preview.status === 'success' && (
              <a
                href={fullUrl(preview.outputUrl)}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="btn btn-primary btn-lg"
                style={{ width: '100%', maxWidth: 320, textDecoration: 'none', textAlign: 'center' }}
              >
                <Icon name="image" size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {t('shopAi.openOriginal')}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
